import { useMemo, useState } from "react";

import clsx from "clsx";
import { sortBy } from "lodash";

import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { salesPageUrl } from "@features/breakdown-v2/utils";
import { useShowV3SalesPage } from "@hooks/http";
import { useDesignElementManager } from "@hooks/use-design-element-manager";
import { type GetLabelAssignment_me_labelAssignment } from "@north-beam/nb-client-side";
import { jsonHash } from "@north-beam/nb-common";
import { TertiaryButton } from "@shared/buttons";
import { CustomCheckbox } from "@shared/custom-checkbox";

import { type Selection } from "..";
import { type Sorting } from "../../edit-breakdown/advanced-editor/types";

import { HeaderMenu } from "./header-menu";
import { SortDirection } from "./sort-direction";

interface Props {
  rows: GetLabelAssignment_me_labelAssignment[];
  totalRows: number;
  sorting: Sorting;
  selection: Selection;
  onChangeSorting: (sorting: Sorting) => void;
  onChangeSelection: (selection: Selection) => void;
}

export const Table = ({
  rows,
  totalRows,
  sorting,
  selection,
  onChangeSorting,
  onChangeSelection,
}: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { user } = useUser();
  const showV3SalesPage = useShowV3SalesPage();
  const { designElementManager } = useDesignElementManager();
  const { value: breakdownConfigs } = useSalesBreakdownConfigs();

  const [expandedRows, setExpandedRows] = useState<string[]>([]);

  const designElementsByLabelKey = useMemo(() => {
    const result: Record<
      string,
      { primaryColor: string; secondaryColor: string }
    > = {};

    sortBy(breakdownConfigs, "key").forEach((breakdown, i) => {
      result[breakdown.key] = {
        primaryColor: designElementManager.getColorForIndex(i),
        secondaryColor: designElementManager.getColorForIndex(i) + "50",
      };
    });

    return result;
  }, [breakdownConfigs, designElementManager]);

  const getDesignElementForLabel = (label: string | null) => {
    return (
      designElementsByLabelKey[label ?? ""] || {
        primaryColor: designElementManager.getColorForIndex(0),
        secondaryColor: designElementManager.getColorForIndex(0) + "50",
      }
    );
  };

  return (
    <table className="text-xs table-fixed	border-spacing-0 border-separate min-w-[100%]">
      <thead className="sticky top-0 bg-white z-[9]">
        <tr>
          <th className="h-[40px] w-[60px] min-w-[60px] border-b border-gray-2">
            <div className="flex items-center pl-2 gap-2 h-full">
              <div className="flex-1 flex items-center justify-center">
                <CustomCheckbox
                  checked={
                    (selection.mode === "inclusive" &&
                      selection.ids.length === totalRows &&
                      totalRows > 0) ||
                    (selection.mode === "exclusive" &&
                      selection.ids.length === 0)
                  }
                  onChange={() => {
                    if (
                      selection.mode === "exclusive" &&
                      selection.ids.length === 0
                    ) {
                      onChangeSelection({ mode: "inclusive", ids: [] });
                    } else {
                      onChangeSelection({ mode: "exclusive", ids: [] });
                    }
                  }}
                />
              </div>
              <div className="border-r border-gray-2 h-3/5 mr-[-1px]"></div>
            </div>
          </th>
          <th className="h-[40px] w-[300px] min-w-[300px] border-b border-gray-2">
            <div className="flex items-center pl-2 gap-2 h-full">
              <div className="flex-1">Name</div>
              <div className="flex items-center gap-1">
                <HeaderMenu name="name" onChange={onChangeSorting} />
                <SortDirection
                  name="name"
                  sorting={sorting}
                  onClick={onChangeSorting}
                />
              </div>
              <div className="border-r border-gray-2 h-3/5 mr-[-1px]"></div>
            </div>
          </th>
          <th className="h-[40px] w-[90px] min-w-[90px] border-b border-gray-2">
            <div className="flex items-center pl-2 gap-2 h-full">
              <div className="flex-1">Status</div>
              <div className="border-r border-gray-2 h-3/5 mr-[-1px]"></div>
            </div>
          </th>
          <th className="h-[40px] w-[130px] min-w-[130px] border-b border-gray-2">
            <div className="flex items-center pl-2 gap-2 h-full">
              <div className="flex-1">Manually overriden</div>
              <div className="border-r border-gray-2 h-3/5 mr-[-1px]"></div>
            </div>
          </th>
          <th className="h-[40px] border-b border-gray-2">
            <div className="flex items-center pl-2 gap-2 h-full">
              <div className="flex-1">Segment</div>
              <div className="border-r border-gray-2 h-3/5 mr-[-1px]"></div>
            </div>
          </th>
          <th className="h-[40px] w-[190px] min-w-[190px] border-b border-gray-2">
            <div className="flex items-center pl-2 gap-2 h-full">
              <div className="flex-1">Actions</div>
              <div className="border-gray-2 h-3/5 mr-[-1px]"></div>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const icon = designElementManager
            .getDesignElement(row.platform ?? "")
            .getComponent({
              size: "24px",
            });
          const labels = sortBy(row.labels, "key");
          const labelsFirstPart = labels.slice(
            0,
            expandedRows.includes(row.adKey) ? Infinity : 3,
          );
          const labelsRest = labels.slice(
            expandedRows.includes(row.adKey) ? Infinity : 3,
          );

          return (
            <tr
              key={row.adKey}
              className="group odd:bg-[#fafafe] even:bg-white"
            >
              <td className="border-r border-gray-2 border-b group-last:border-b-0">
                <div className="p-2 flex items-center justify-center">
                  <CustomCheckbox
                    checked={
                      (selection.mode === "inclusive" &&
                        selection.ids.includes(row.adKey)) ||
                      (selection.mode === "exclusive" &&
                        !selection.ids.includes(row.adKey))
                    }
                    onChange={() => {
                      if (selection.ids.includes(row.adKey)) {
                        onChangeSelection({
                          ...selection,
                          ids: selection.ids.filter((e) => e !== row.adKey),
                        });
                      } else {
                        let mode = selection.mode;
                        let ids = [...selection.ids, row.adKey];

                        if (
                          selection.mode === "exclusive" &&
                          ids.length === totalRows
                        ) {
                          mode = "inclusive";
                          ids = [];
                        }

                        if (
                          selection.mode === "inclusive" &&
                          ids.length === totalRows
                        ) {
                          mode = "exclusive";
                          ids = [];
                        }

                        onChangeSelection({ mode, ids });
                      }
                    }}
                  />
                </div>
              </td>
              <td className="border-r border-gray-2 border-b group-last:border-b-0">
                <div className="font-bold p-2 flex items-center gap-2">
                  <div>
                    <button
                      onClick={() => {
                        prettyLogEventPage({
                          domain: "Breakdown v2",
                          action: "onClick",
                          additionalActionInfo: "Expand label assignment row",
                          data: {
                            pageContext: { adKey: row.adKey },
                          },
                        });

                        if (expandedRows.includes(row.adKey)) {
                          setExpandedRows(
                            expandedRows.filter((e) => e !== row.adKey),
                          );
                        } else {
                          setExpandedRows([...expandedRows, row.adKey]);
                        }
                      }}
                      className={clsx(
                        "w-6 h-6 text-xs text-cosmic rounded-lg flex items-center justify-center",
                        "hover:text-cosmic+1 hover:bg-cosmic-2",
                      )}
                    >
                      <i
                        className={clsx(
                          "fa-regular",
                          expandedRows.includes(row.adKey)
                            ? "fa-chevron-down"
                            : "fa-chevron-right",
                        )}
                      />
                    </button>
                  </div>
                  {icon}
                  <div>{row.name}</div>
                </div>
              </td>
              <td className="border-r border-gray-2 border-b group-last:border-b-0">
                <div className="p-2 flex items-center gap-2">
                  {row.status && (
                    <span
                      className={clsx(
                        "py-0.5 px-3 rounded capitalize text-sm",
                        {
                          "bg-alien-2 text-[#3B8139]":
                            row.status.match("Active"),
                          "bg-interstellar-3": !row.status.match("Active"),
                        },
                      )}
                    >
                      {row.status.match("Active")
                        ? "Active"
                        : row.status.length > 0
                        ? "Inactive"
                        : ""}
                    </span>
                  )}
                </div>
              </td>
              <td className="border-r border-gray-2 border-b group-last:border-b-0">
                <div className="p-2 flex items-center gap-2">
                  {row.labels.some((e) => e.isManualLabel) && (
                    <div
                      className={clsx(
                        "py-0.5 px-2.5 rounded capitalize bg-mars-2 text-sm text-mars+2",
                        "inline-flex items-center gap-2",
                      )}
                    >
                      <i className="fa-regular fa-pen text-xs" /> Yes
                    </div>
                  )}
                </div>
              </td>
              <td className="border-r border-gray-2 border-b group-last:border-b-0">
                <div className="p-2 flex items-start justify-between gap-4">
                  <div className="flex flex-wrap items-start gap-2">
                    {labelsFirstPart.map((label) => (
                      <GenericTooltip
                        content="Label has been manually assigned"
                        showTooltip={label.isManualLabel}
                        key={jsonHash({ adKey: row.adKey, label })}
                        noInfoCircle
                      >
                        <div
                          className={clsx(
                            "relative border rounded py-[3px] px-2.5 text-xs space-x-1",
                          )}
                          style={{
                            backgroundColor: getDesignElementForLabel(label.key)
                              .secondaryColor,
                            borderColor: getDesignElementForLabel(label.key)
                              .primaryColor,
                          }}
                        >
                          {label.isManualLabel && (
                            <span
                              className={clsx(
                                "absolute right-[-8px] top-[-8px] w-4 h-4 bg-mars rounded-full",
                                "flex items-center justify-center",
                              )}
                            >
                              <i className="fa-solid fa-pen text-white text-xxxs" />
                            </span>
                          )}
                          <span>{label.key}:</span>
                          <span className="font-bold">{label.value}</span>
                        </div>
                      </GenericTooltip>
                    ))}
                    {labelsRest.length > 0 && (
                      <div
                        className={clsx(
                          "border border-gray bg-interstellar-3 rounded text-xs py-[3px] px-2",
                        )}
                      >
                        +{labelsRest.length}
                      </div>
                    )}
                  </div>
                </div>
              </td>
              <td className="border-gray-2 border-b group-last:border-b-0">
                <div className="p-2 flex items-center gap-2">
                  <TertiaryButton
                    onClick={() => {
                      prettyLogEventPage({
                        domain: "Breakdown v2",
                        action: "onClick",
                        additionalActionInfo: "View in sales page",
                        data: {
                          pageContext: { adKey: row.adKey },
                        },
                      });

                      window
                        ?.open(
                          salesPageUrl({
                            adKey: JSON.parse(row.adKey),
                            name: row.name,
                            showV3SalesPage,
                            displayUserId: user.displayUserId,
                          }),
                          "_blank",
                        )
                        ?.focus();
                    }}
                    className="flex items-center gap-2 font-bold bg-cosmic-4 hover:bg-cosmic-3"
                  >
                    <i className="fa-regular fa-eye" /> VIEW IN SALES PAGE
                  </TertiaryButton>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
